import { useAuth0 } from '@auth0/auth0-react';
import { useAvailableAccounts } from '@my/directory-api/hooks';
import { useAccount } from '@my/platform/account';
import { clearSupportSession, useSupportSession } from '@my/platform/support';
import { AccountSwitcher, Sidebar, SupportAccountLabel, type SidebarItem } from '@my/ui';
import { Boxes, Building2, Landmark, Users } from 'lucide-react';
import { useUiStore } from '@/stores/ui';

function getDisplayName(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return '';
  return (user.name ?? user.email ?? '').trim();
}

/**
 * My Hub's navigation.
 *
 * <p>
 * SHAPED FOR THE CUSTOMER WHO WILL EVENTUALLY USE IT. The four sections are
 * what an account owner manages about their own account, and they are the whole
 * menu — no route carries an account id, because an owner has none to carry.
 * Which account they describe is answered above and below them: the ordinary
 * switcher in this header, or the Stratum strip at the bottom of the shell.
 * </p>
 */
export function AppSidebar() {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { currentAccountId, setCurrentAccountId } = useAccount();
  const { isSupportSession, session } = useSupportSession();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);

  // The user's OWN accounts. 404s for a Stratum identity that belongs to none,
  // so it is not even asked for during a support session.
  const accountsQuery = useAvailableAccounts({ enabled: !isSupportSession });
  const accounts = (accountsQuery.data ?? []).map((a) => ({ id: a.id, name: a.name }));

  const handleSwitch = async (accountId: string) => {
    setCurrentAccountId(accountId);
    // Re-mint the token against the new account — the platform account store
    // only records the choice, the Action is what makes it true.
    await getAccessTokenSilently({
      authorizationParams: { account_id: accountId },
      cacheMode: 'off',
    });
  };

  const items: SidebarItem[] = [
    { id: 'account', label: 'Account', icon: Building2, href: '/account', matchRoot: true },
    { id: 'subscriptions', label: 'Subscriptions', icon: Boxes, href: '/subscriptions' },
    { id: 'organisations', label: 'Organisations', icon: Landmark, href: '/organisations' },
    { id: 'users', label: 'Users', icon: Users, href: '/users' },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onToggle={toggle}
      brand={{ title: 'My Stratum', icon: Landmark }}
      headerSlot={
        // During a support session the switcher is replaced, not hidden: it
        // moves between the USER'S OWN accounts and reads `/accounts/available`,
        // which 404s for a Stratum identity — so it would hang on "Loading…".
        // The label keeps the brand block aligned and still says whose data this
        // is; changing customer stays on the support strip.
        isSupportSession ? (
          <SupportAccountLabel accountName={session?.accountName} />
        ) : (
          <AccountSwitcher
            accounts={accounts}
            currentAccountId={currentAccountId}
            onSwitch={handleSwitch}
            isLoading={accountsQuery.isPending}
          />
        )
      }
      items={items}
      user={{ displayName: getDisplayName(user), role: isSupportSession ? 'superuser' : 'owner' }}
      onSignOut={() => {
        // Clear FIRST. A support session lives in sessionStorage, which survives
        // a sign-out on the same tab — leaving the next person to sign in here
        // carrying somebody else's customer, and their screens pointed at that
        // customer's organisation.
        clearSupportSession({ silent: true });
        logout({ logoutParams: { returnTo: window.location.origin } });
      }}
    />
  );
}
